import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { 
  MessageSquare, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Star,
  Bug,
  Lightbulb,
  Heart,
  Loader
} from 'lucide-react'

const Feedback: React.FC = () => {
  const [formData, setFormData] = useState({
    type: 'general',
    name: '',
    email: '',
    subject: '',
    message: '',
    rating: 0,
    priority: 'medium'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')

  const feedbackTypes = [
    {
      id: 'general',
      name: '一般反馈',
      icon: MessageSquare,
      description: '对产品或服务的整体反馈',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'bug',
      name: 'Bug报告',
      icon: Bug,
      description: '发现的问题或错误',
      color: 'from-red-500 to-pink-500'
    },
    {
      id: 'feature',
      name: '功能建议',
      icon: Lightbulb,
      description: '新功能或改进建议',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      id: 'praise',
      name: '表扬',
      icon: Heart,
      description: '对团队或产品的表扬',
      color: 'from-green-500 to-emerald-500'
    }
  ]

  const priorities = [
    { id: 'low', name: '低', description: '不影响正常使用' },
    { id: 'medium', name: '中', description: '影响部分功能' },
    { id: 'high', name: '高', description: '严重影响使用' },
    { id: 'urgent', name: '紧急', description: '完全无法使用' }
  ]

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')

    try {
      // 这里可以调用API提交反馈
      await new Promise(resolve => setTimeout(resolve, 2000)) // 模拟API调用
      
      setSubmitStatus('success')
      setSubmitMessage('反馈提交成功！感谢您的宝贵意见，我们会认真考虑并尽快回复。')
      
      // 重置表单
      setFormData({
        type: 'general',
        name: '',
        email: '',
        subject: '',
        message: '',
        rating: 0,
        priority: 'medium'
      })
    } catch (error) {
      setSubmitStatus('error')
      setSubmitMessage('提交失败，请稍后重试或通过其他方式联系我们。')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>用户反馈 - DeepLink Limited</title>
        <meta name="description" content="提交您对DeepLink产品和服务的反馈，帮助我们不断改进。" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <section className="py-20 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <MessageSquare className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                用户反馈
              </h1>
              <p className="text-xl text-white/90 max-w-3xl mx-auto">
                您的意见对我们非常重要，帮助我们不断改进产品和服务
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Feedback Types */}
              <div className="lg:col-span-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  反馈类型
                </h2>
                <div className="space-y-4">
                  {feedbackTypes.map((type) => {
                    const Icon = type.icon
                    return (
                      <button
                        key={type.id}
                        onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
                        className={`w-full p-4 rounded-2xl text-left transition-all duration-300 ${
                          formData.type === type.id
                            ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            formData.type === type.id ? 'bg-white/20' : `bg-gradient-to-r ${type.color}`
                          }`}>
                            <Icon className={`w-5 h-5 ${formData.type === type.id ? 'text-white' : 'text-white'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{type.name}</h3>
                            <p className="text-sm opacity-80">{type.description}</p>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Feedback Form */}
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Basic Info */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                      基本信息
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          姓名 *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          邮箱 *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Feedback Details */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                      反馈详情
                    </h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          主题 *
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="简要描述您的反馈"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          详细描述 *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          required
                          rows={6}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          placeholder="请详细描述您的反馈内容..."
                        />
                      </div>

                      {/* Rating */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          满意度评分
                        </label>
                        <div className="flex space-x-2">
                          {[1, 2, 3, 4, 5].map((rating) => (
                            <button
                              key={rating}
                              type="button"
                              onClick={() => handleRatingChange(rating)}
                              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                                rating <= formData.rating
                                  ? 'bg-yellow-400 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-400'
                              }`}
                            >
                              <Star className="w-5 h-5" />
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Priority */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          优先级
                        </label>
                        <select
                          name="priority"
                          value={formData.priority}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        >
                          {priorities.map((priority) => (
                            <option key={priority.id} value={priority.id}>
                              {priority.name} - {priority.description}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Submit Status */}
                  {submitMessage && (
                    <div className={`p-4 rounded-lg flex items-center ${
                      submitStatus === 'success' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                        : submitStatus === 'error'
                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                        : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    }`}>
                      {submitStatus === 'success' ? (
                        <CheckCircle className="w-5 h-5 mr-2" />
                      ) : submitStatus === 'error' ? (
                        <AlertCircle className="w-5 h-5 mr-2" />
                      ) : (
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                      )}
                      {submitMessage}
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <Loader className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    {isSubmitting ? '提交中...' : '提交反馈'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="py-20 bg-white dark:bg-gray-800">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                其他联系方式
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
                您也可以通过以下方式联系我们
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">邮箱</h3>
                  <p className="text-gray-600 dark:text-gray-400">feedback@deeplink.hk</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">电话</h3>
                  <p className="text-gray-600 dark:text-gray-400">+852 1234-5678</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">地址</h3>
                  <p className="text-gray-600 dark:text-gray-400">香港中环国际金融中心二期</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default Feedback